import { PaymentElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { FormEvent, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import services from 'services';

import {
    confirmB2BCheckout,
    confirmCheckout,
    selectedB2BDeliveryAddress,
    selectSelectedShippingOptionId,
} from 'store/reducers/cartSlice';
import CheckoutButton from '../CheckoutButton';
import { clearPaymentReturnData, savePaymentReturnData } from './paymentReturnStorage';

type PaymentFormProps = {
    onPaid: () => void;
    onBusyChange: (busy: boolean) => void;
    onPaymentCompleted: () => void;
};

const PaymentForm = ({ onPaid, onBusyChange, onPaymentCompleted }: PaymentFormProps) => {
    const checkoutState = useCheckoutElements();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { address } = useAppSelector(({ cart }) => cart.standardCheckout);
    const { delivery } = useAppSelector(({ cart }) => cart.b2bCheckout);
    const selectedDeliveryAddress = useAppSelector(selectedB2BDeliveryAddress);
    const isB2B = useAppSelector(({ shop }) => shop?.shop?.isB2BShop) ?? false;
    const shippingOptionId = useAppSelector(selectSelectedShippingOptionId) ?? undefined;

    const [submitting, setSubmitting] = useState(false);
    const [elementReady, setElementReady] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [orderFailed, setOrderFailed] = useState(false);

    const checkoutReady = checkoutState.type === 'success';
    const checkoutLoadError = checkoutState.type === 'error' ? checkoutState.error.message : null;

    const setBusy = (busy: boolean) => {
        setSubmitting(busy);
        onBusyChange(busy);
    };

    const placeOrder = async (): Promise<void> => {
        try {
            const result = isB2B
                ? await dispatch(
                      confirmB2BCheckout({
                          b2bCheckout: { delivery: { ...delivery, ...selectedDeliveryAddress } },
                      }),
                  ).unwrap()
                : await dispatch(confirmCheckout({ standardCheckout: address })).unwrap();

            if (!result) {
                setOrderFailed(true);
                return;
            }

            onPaid();
        } catch {
            setOrderFailed(true);
        }
    };

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (submitting || checkoutState.type !== 'success') return;

        setBusy(true);
        setPaymentError(null);

        try {
            const sessionEmail =
                (isB2B ? delivery?.email || selectedDeliveryAddress?.email : address?.email)?.trim() || '';

            if (!sessionEmail) {
                setPaymentError(t('payment_emailRequired', 'Email is required to complete payment.'));
                return;
            }

            let amountIsValid: boolean;
            try {
                amountIsValid = await services.stripe.validateCheckoutSessionAmount(shippingOptionId);
            } catch {
                setPaymentError(t('payment_initFailed'));
                return;
            }

            if (!amountIsValid) {
                setPaymentError(
                    t('payment_amountChanged', 'The order total has changed. Return to your cart and restart payment.'),
                );
                return;
            }

            if (isB2B) {
                savePaymentReturnData({
                    isB2B: true,
                    delivery: { ...delivery, ...selectedDeliveryAddress },
                    shippingOptionId,
                });
            } else {
                savePaymentReturnData({ isB2B: false, standardCheckout: address, shippingOptionId });
            }

            const result = await checkoutState.checkout.confirm({
                redirect: 'if_required',
                email: sessionEmail,
            });

            if (result.type === 'error') {
                clearPaymentReturnData();
                setPaymentError(result.error?.message || t('payment_failed'));
                return;
            }

            clearPaymentReturnData();
            onPaymentCompleted();
            await placeOrder();
        } finally {
            setBusy(false);
        }
    };

    const retryOrder = async () => {
        if (submitting) return;
        setBusy(true);
        await placeOrder();
        setBusy(false);
    };

    if (orderFailed) {
        return (
            <>
                <Alert variant="warning">{t('payment_orderFailedAfterPayment')}</Alert>
                <CheckoutButton
                    text={t('payment_completeOrder', 'Complete order')}
                    loading={submitting}
                    onSubmit={retryOrder}
                    bgColor="buy"
                    disabledColor="textLight"
                />
            </>
        );
    }

    return (
        <form onSubmit={submitHandler}>
            <PaymentElement onReady={() => setElementReady(true)} onLoadError={() => setLoadError(true)} />
            {loadError && (
                <Alert variant="danger" className="mt-3">
                    {t('payment_initFailed')}
                </Alert>
            )}
            {paymentError && (
                <Alert variant="danger" className="mt-3">
                    {paymentError}
                </Alert>
            )}
            {checkoutLoadError && (
                <Alert variant="danger" className="mt-3">
                    {checkoutLoadError}
                </Alert>
            )}
            <CheckoutButton
                text={t('payment_pay', 'Pay now')}
                loading={submitting}
                disabled={!checkoutReady || !elementReady || loadError}
                bgColor="buy"
                disabledColor="textLight"
            />
        </form>
    );
};

export default PaymentForm;
